// External libraries
import Grid from '@mui/material/Grid2';
import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AddOutlined from '@mui-symbols-material/w400/AddOutlined';
import CloseOutlinedIcon from '@mui-symbols-material/w400/CloseOutlined';
import OpenInFullOutlinedIcon from '@mui-symbols-material/w400/OpenInFullOutlined';
import CloseFullscreenOutlinedIcon from '@mui-symbols-material/w400/CloseFullscreenOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

// Styles
import styles from './TextDetailDialog.module.scss';

// API
import {
  useCreateTextMutation,
  useUpdateTextMutation,
  useLazyFetchTextScopesQuery,
  useCreateTextScopeMutation,
  useDeleteTextScopeMutation
} from '@api/texts/texts-api';

// Models
import { TextApiModel } from '@models/ApiModels/text-api-model';
import { TextScope } from '@models/enums/TextScope';
import { TextType } from '@models/enums/TextType';

// Common components
import { CdfFormField } from '@commonComponents/forms/fields/CdfFormField';
import { CdfFormattedTextFormField } from '@commonComponents/forms/fields/CdfFormattedTextFormField';
import { EntityDetailDialog, EntityDetailDialogWrapper } from '@commonComponents/dialogs/EntityDetailDialog';

// State management
import { DynamicStore } from '@state/interfaces/idynamic-store';

// Form Validation
import { FormFactory } from '@services/validation/form-factory';
import { useDecoratorForm } from '@hooks/useDecoratorForm';
import { useEntityDetail, EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

// Permissions
import { useFetchToken } from '@hooks/useFetchToken';

// Logging
import { logger } from '@utils/logging-utils';
import { Text } from '@models/DomainModels/Master/text';

type TextDetailDialogProps = {
  textData?: Text;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<Text>) => void;
  /** Redux store */
  store: DynamicStore | undefined;
  /** Auth token for API calls */
  token: string;
  /** Whether user has read permission */
  readPermission?: boolean;
  /** Whether user has write permission */
  writePermission?: boolean;
};

/**
 * Text Detail Dialog component
 *
 * Dialog for creating and editing text entities.
 *
 * @param props Component props
 * @returns React component
 */
function TextDetailDialog({
  textData,
  organizationId,
  open,
  onClose,
  token,
  readPermission = false,
  writePermission = false
}: Readonly<TextDetailDialogProps>) {
  const { t } = useTranslation(['texts', 'common', 'enums']);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [existingTextScopes, setExistingTextScopes] = useState<{ id: string; scope: string }[]>([]);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // RTK Query hooks for API operations
  const [createText] = useCreateTextMutation();
  const [updateText] = useUpdateTextMutation();
  const [fetchTextScopes] = useLazyFetchTextScopesQuery();
  const [createTextScope] = useCreateTextScopeMutation();
  const [deleteTextScope] = useDeleteTextScopeMutation();

  // Create array of available text scopes from enum
  const availableScopes = useMemo(
    () =>
      Object.entries(TextScope).map(([key, value]) => ({
        key,
        value
      })),
    []
  );

  // Create array of available text types from enum
  const availableTypes = useMemo(
    () =>
      Object.entries(TextType).map(([key, value]) => ({
        key,
        value
      })),
    []
  );

  // Initial state for a new text entity
  const initialText = useMemo(
    () => ({
      number: 0,
      name: '',
      type: '',
      value: '',
      formattedValue: ''
    }),
    []
  );

  // Form configuration using FormFactory
  const formConfig = useMemo(
    () =>
      FormFactory.getFormConfig(TextApiModel, textData ?? initialText, {
        showSnackbarOnError: true
      }),
    [textData, initialText]
  );

  // Form state using decorator validation
  const {
    data: text,
    errors: validationErrors,
    isValid: formIsValid,
    updateField,
    validateField,
    validateForm,
    resetForm,
    setData,
    setFormChanged,
    isDirty
  } = useDecoratorForm(formConfig.modelClass, formConfig.initialData, formConfig.options);

  // Fetch existing text scopes when editing
  useEffect(() => {
    const loadTextScopes = async () => {
      if (!textData?.id || !open) return;

      try {
        const result = await fetchTextScopes({
          organizationId,
          textId: textData.id,
          token
        }).unwrap();

        if (result?.data) {
          setExistingTextScopes(result.data);
          setSelectedScopes(result.data.map(scope => scope.scope));
        }
      } catch (error) {
        logger.error('TextDetailDialog', 'Failed to fetch text scopes', { error, textId: textData.id, organizationId });
      }
    };

    loadTextScopes();
  }, [textData?.id, open, organizationId, token, fetchTextScopes]);

  // Check for required fields having values
  // This ensures the save button is disabled until all required fields have values
  const hasRequiredFieldValues = useMemo(() => {
    return (
      // Check for number (required and must be > 0)
      !!text.number &&
      text.number > 0 &&
      // Check for name (required)
      !!text.name?.trim() &&
      // Check for type (required)
      !!text.type?.trim() &&
      // Check for value (required)
      !!text.value?.trim()
    );
  }, [text.number, text.name, text.type, text.value]);

  // Combine validations - the form is valid only when all required fields are filled AND validation passes
  const isValid = useMemo(() => {
    return formIsValid && hasRequiredFieldValues;
  }, [formIsValid, hasRequiredFieldValues]);

  // Wrap validateForm to handle potential async validation
  const validateFormWrapper = useCallback(() => {
    return validateForm();
  }, [validateForm]);

  // Convert isDirty function to a boolean value
  const isDirtyValue = useMemo(() => isDirty(), [isDirty]);

  // Handle scope selection
  const handleAddScope = useCallback(
    (scopeValue: string) => {
      if (!selectedScopes.includes(scopeValue)) {
        setSelectedScopes(prev => [...prev, scopeValue]);
      }
    },
    [selectedScopes]
  );

  // Handle scope removal
  const handleDeleteScope = useCallback(
    (scopeValue: string) => {
      if (!writePermission) return;
      setSelectedScopes(prev => prev.filter(scope => scope !== scopeValue));
    },
    [writePermission]
  );

  // Handle opening the scope selection dialog
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);

  // Handle fullscreen toggle
  const handleToggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => !prev);
  }, []);

  const createEntity = useCallback(async (): Promise<Text> => {
    const newTextId = await createText({
      organizationId,
      textData: {
        number: text.number ?? 0,
        name: text.name ?? '',
        type: text.type ?? '',
        value: text.value ?? '',
        formattedValue: text.formattedValue ?? undefined
      },
      token
    }).unwrap();

    const extractedId = newTextId.split('/').pop() ?? newTextId;

    if (selectedScopes.length > 0) {
      for (const scope of selectedScopes) {
        await createTextScope({
          organizationId,
          textId: extractedId,
          textScopeData: { scope },
          token
        });
      }
    }

    return { ...text, id: extractedId, organizationId } as Text;
  }, [createText, createTextScope, organizationId, selectedScopes, token, text]);

  const updateEntity = useCallback(async (): Promise<Text> => {
    await updateText({
      organizationId,
      textId: textData?.id ?? '',
      textData: {
        number: text.number ?? 0,
        name: text.name ?? '',
        type: text.type ?? '',
        value: text.value ?? '',
        formattedValue: text.formattedValue ?? undefined
      },
      token
    }).unwrap();

    const existingScopeSet = new Set(existingTextScopes.map(scope => scope.scope));
    const selectedScopeSet = new Set(selectedScopes);

    const scopesToRemove = existingTextScopes.filter(s => !selectedScopeSet.has(s.scope));
    const scopesToAdd = selectedScopes.filter(s => !existingScopeSet.has(s));

    for (const scopeToRemove of scopesToRemove) {
      await deleteTextScope({ organizationId, textId: textData?.id ?? '', textScopeId: scopeToRemove.id, token });
    }

    for (const scopeToAdd of scopesToAdd) {
      await createTextScope({ organizationId, textId: textData?.id ?? '', textScopeData: { scope: scopeToAdd }, token });
    }

    return { ...text, id: textData?.id ?? '', organizationId } as Text;
  }, [updateText, organizationId, textData?.id, token, existingTextScopes, selectedScopes, deleteTextScope, createTextScope, text]);

  // Check if scopes have been modified compared to initial state
  const scopesModified = useMemo(() => {
    if (!textData?.id) {
      // For new text, any selected scopes means modification
      return selectedScopes.length > 0;
    }

    // For existing text, compare selected scopes with existing scopes
    if (selectedScopes.length !== existingTextScopes.length) {
      return true;
    }

    // Check if all selected scopes exist in existingTextScopes
    const existingScopeValues = existingTextScopes.map(scope => scope.scope);
    // More explicit boolean logic - convert both sides to boolean results
    return (
      Boolean(selectedScopes.some(scope => !existingScopeValues.includes(scope))) ||
      Boolean(existingScopeValues.some(scope => !selectedScopes.includes(scope)))
    );
  }, [textData?.id, selectedScopes, existingTextScopes]);

  const combinedIsDirty = Boolean(scopesModified) || Boolean(isDirtyValue);

  const handleDialogClose = useCallback(
    (result: EntityDialogCloseResult<Text>) => {
      resetForm();
      setFormChanged(false);
      setSelectedScopes([]);
      setExistingTextScopes([]);
      setIsFullScreen(false);
      onClose(result);
    },
    [onClose, resetForm, setFormChanged]
  );

  const { handleSave, loadingState } = useEntityDetail<Text>({
    entityName: t('entityName'),
    initialData: text as Text,
    open,
    onClose: handleDialogClose,
    createEntity,
    updateEntity,
    validateForm: validateFormWrapper,
    setData: data => setData(data as Partial<TextApiModel>, true),
    resetForm,
    loggerContext: 'TextDetailDialog',
    readPermission,
    writePermission,
    setFormChanged
  });

  // Create a wrapped handleSave function that includes logging
  const handleSaveWithLogging = useCallback(async () => {
    try {
      await handleSave();
    } catch (error) {
      logger.error('TextDetailDialog', 'Save operation failed', {
        textId: textData?.id,
        error
      });
    }
  }, [handleSave, textData?.id]);

  const handleFieldChange = useCallback(
    (field: string, value: string | number) => {
      const processedValue = field === 'number' ? Number(value) || 0 : value;
      updateField(field, processedValue);
      setTimeout(() => validateField(field, processedValue), 0);
    },
    [updateField, validateField]
  );

  // Handle rich text editor update – updates both plain-text `value` and formatted HTML `formattedValue`
  const handleRichTextUpdate = useCallback(
    (_name: string, plainText: string, html: string) => {
      updateField('value', plainText);
      updateField('formattedValue', html);
      setTimeout(() => validateField('value', plainText));
    },
    [updateField, validateField]
  );

  // Handle field blur (validation)
  const handleFieldBlur = useCallback(
    (field: string) => {
      validateField(field, text[field as keyof typeof text]);
    },
    [validateField, text]
  );

  // Create form content
  const formContent = (
    <Grid container ref={dialogRef}>
      {/* Number, Name and Type Fields in a single row */}
      <Grid size={12} container>
        <Grid size={12}>
          <CdfFormField
            name="number"
            label={t('detailDialog.number')}
            value={text.number}
            required
            type="number"
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            errors={validationErrors}
            data-testid="number-field"
            slotProps={{
              input: {
                'aria-label': t('detailDialog.number'),
                readOnly: !writePermission
              }
            }}
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <CdfFormField
            name="name"
            label={t('detailDialog.name')}
            value={text.name}
            required
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            errors={validationErrors}
            data-testid="name-field"
            slotProps={{
              input: {
                'aria-label': t('detailDialog.name'),
                readOnly: !writePermission
              }
            }}
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <CdfFormField
            name="type"
            label={t('detailDialog.type')}
            value={text.type}
            required
            select
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            errors={validationErrors}
            data-testid="type-field"
            slotProps={{
              input: {
                'aria-label': t('detailDialog.type'),
                readOnly: !writePermission
              }
            }}
            fullWidth>
            {availableTypes.map(option => (
              <MenuItem key={option.key} value={option.value}>
                {t(`enums:textType.${option.key}`)}
              </MenuItem>
            ))}
          </CdfFormField>
        </Grid>
      </Grid>

      {/* Text Scopes Section - Fieldset Style */}
      <Grid size={12}>
        <Box className={styles.scopesContainer}>
          {/* Label positioned on the border */}
          <Typography variant="caption" className={styles.scopesLabel}>
            {t('headers.scopes')}
          </Typography>

          <Box className={styles.scopesContent}>
            <div className={styles.scopesStack} data-testid="text-scopes-stack">
              {/* Display scope chips with delete option */}
              {selectedScopes.map(scope => {
                // Get enum key for translation
                const scopeKey = Object.entries(TextScope).find(([, val]) => val === scope)?.[0];
                return (
                  <Chip
                    key={scope}
                    label={scopeKey ? t(`enums:textScope.${scopeKey}`) : scope}
                    size="small"
                    className={styles.scopeChip}
                    onDelete={() => handleDeleteScope(scope)}
                    deleteIcon={<CloseOutlinedIcon fontSize="small" aria-label={t('detailDialog.deleteScopeAriaLabel', { scope })} />}
                    data-testid={`scope-chip-${scope}`}
                  />
                );
              })}

              {/* Button to add new scope - integrated into the chip row */}
              {writePermission && (
                <IconButton
                  size="small"
                  className={styles.addButton}
                  onClick={() => setScopeDialogOpen(true)}
                  data-testid="add-scope-button"
                  aria-label={t('detailDialog.addScopeAriaLabel')}>
                  <AddOutlined fontSize="small" />
                </IconButton>
              )}
            </div>
          </Box>
        </Box>

        {/* Scope Selection Dialog */}
        {writePermission && (
          <Dialog
            open={scopeDialogOpen}
            onClose={() => setScopeDialogOpen(false)}
            aria-labelledby="scope-selection-dialog-title"
            data-testid="scope-selection-dialog"
            maxWidth="xs"
            fullWidth>
            <DialogTitle id="scope-selection-dialog-title">{t('detailDialog.selectScope', 'Select Scope')}</DialogTitle>
            <DialogContent>
              <List sx={{ pt: 1 }}>
                {availableScopes
                  .filter(scope => !selectedScopes.includes(scope.value))
                  .map(scope => (
                    <ListItem
                      key={scope.key}
                      onClick={() => {
                        handleAddScope(scope.value);
                        setScopeDialogOpen(false);
                      }}
                      className={styles.scopeListItem}
                      data-testid={`scope-option-${scope.value}`}>
                      <ListItemText primary={t(`enums:textScope.${scope.key}`)} />
                    </ListItem>
                  ))}
              </List>
            </DialogContent>
          </Dialog>
        )}
      </Grid>

      {/* Value Field */}
      <Grid size={12}>
        <CdfFormattedTextFormField
          name="value"
          label={t('detailDialog.value')}
          required
          readOnly={!writePermission}
          errors={validationErrors}
          value={text.formattedValue ?? text.value}
          editorMinHeight={isFullScreen ? '21rem' : '3rem'}
          data-testid="value-field"
          onUpdate={handleRichTextUpdate}
        />
      </Grid>
    </Grid>
  );

  // Get save button text based on edit vs. create mode
  const saveButtonText = textData?.id ? t('saveChanges') : t('add');

  // Get cancel button text based on write permission
  const cancelButtonText = writePermission ? t('common:cancel') : t('common:close');

  // Get edit mode display title based on permissions
  const editModeTitle = writePermission ? t('edit') : t('view');

  // Get title text based on whether we're editing or creating
  const titleText = textData?.id ? editModeTitle : t('add');

  // Create action buttons for the dialog header
  const actionButtons = (
    <IconButton
      onClick={handleToggleFullScreen}
      aria-label={isFullScreen ? t('common:exitFullscreen') : t('common:enterFullscreen')}
      data-testid="text-detail-fullscreen-toggle"
      size="small">
      {isFullScreen ? <CloseFullscreenOutlinedIcon fontSize="small" /> : <OpenInFullOutlinedIcon fontSize="small" />}
    </IconButton>
  );

  return (
    <EntityDetailDialog
      open={open}
      onClose={() => handleDialogClose({ action: 'cancelled' })}
      title={titleText}
      formContent={formContent}
      saveButtonText={saveButtonText}
      cancelButtonText={cancelButtonText}
      readPermission={readPermission}
      writePermission={writePermission}
      isLoading={false}
      loadingState={loadingState}
      loadingMessage={t('detailDialog.loadingMessage')}
      savingMessage={t('detailDialog.savingMessage')}
      isValid={isValid}
      isDirty={combinedIsDirty}
      onSave={handleSaveWithLogging}
      dataTestId="text-detail-dialog"
      maxWidth={isFullScreen ? false : 'sm'}
      fullScreen={isFullScreen}
      actionButtons={actionButtons}
    />
  );
}

type TextDetailDialogWrapperProps = {
  textData?: Text;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<Text>) => void;
  organizationId: string;
  store: DynamicStore | undefined;
};

export function TextDetailDialogWrapper({ textData, open, onClose, organizationId, store }: Readonly<TextDetailDialogWrapperProps>) {
  const token = useFetchToken();

  return (
    <EntityDetailDialogWrapper
      organizationId={organizationId}
      store={store}
      loggerContext="TextDetailDialog"
      dataTestId="text-detail-error-boundary-wrapper"
      entityType="Texts"
      renderContent={permissions => (
        <TextDetailDialog
          textData={textData}
          open={open}
          onClose={onClose}
          organizationId={organizationId}
          store={store}
          token={token}
          readPermission={permissions.readPermission}
          writePermission={permissions.writePermission}
        />
      )}
    />
  );
}
